import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Database, UserCheck, Trash2, Download, Clock, Mail, Globe, Eye } from 'lucide-react';
import Footer from '../components/Footer';

const PrivacyPolicyPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-bg to-fg/5 py-20">
      <div className="container mx-auto px-6 max-w-4xl">
      <Link
        to="/"
        className="relative z-[9999] inline-flex items-center gap-2 text-primary mb-6 hover:underline cursor-pointer"
      >
        <ArrowLeft size={20} />
        {t('backToHome')}
      </Link>
        <div className="bg-white dark:bg-fg/10 rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-primary to-secondary p-8 text-white">
            <div className="flex items-center gap-3 mb-4">
              <Shield size={32} />
              <h1 className="text-3xl md:text-4xl font-bold">{t('privacyPolicy')}</h1>
            </div>
            <p className="opacity-90">{t('privacyPolicySubtitle')}</p>
            <p className="text-sm opacity-75 mt-2">{t('policyLastUpdated')}: 15.05.2026</p>
          </div>

          <div className="p-6 md:p-8 space-y-6 text-fg/80">
            
            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">1. {t('introTitle')}</h2>
              <p className="leading-relaxed">{t('introText')}</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3 flex items-center gap-2">
                <Database size={20} /> 2. {t('whatDataWeCollect')}
              </h2>
              <p className="leading-relaxed mb-2">{t('dataCollectDesc')}</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>{t('dataPoint1')}</li>
                <li>{t('dataPoint2')}</li>
                <li>{t('dataPoint3')}</li>
                <li>{t('dataPoint4')}</li>
                <li>{t('dataPoint5')}</li>
                <li>{t('dataPoint6')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">3. {t('howWeUseData')}</h2>
              <p className="leading-relaxed mb-2">{t('dataUseDesc')}</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>{t('usePoint1')}</li>
                <li>{t('usePoint2')}</li>
                <li>{t('usePoint3')}</li>
                <li>{t('usePoint4')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">4. {t('legalBasis')}</h2>
              <p className="leading-relaxed">{t('legalBasisText')}</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3 flex items-center gap-2">
                <UserCheck size={20} /> 5. {t('yourRights')}
              </h2>
              <p className="leading-relaxed mb-2">{t('rightsDesc')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <div className="flex items-start gap-2 p-3 bg-primary/5 rounded-lg">
                  <Eye size={18} className="text-primary mt-0.5" />
                  <div>
                    <strong>{t('rightToAccess')}</strong>
                    <p className="text-sm text-fg/60">{t('rightToAccessDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-3 bg-primary/5 rounded-lg">
                  <Download size={18} className="text-primary mt-0.5" />
                  <div>
                    <strong>{t('rightToPortability')}</strong>
                    <p className="text-sm text-fg/60">{t('rightToPortabilityDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-3 bg-primary/5 rounded-lg">
                  <Trash2 size={18} className="text-primary mt-0.5" />
                  <div>
                    <strong>{t('rightToErase')}</strong>
                    <p className="text-sm text-fg/60">{t('rightToEraseDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-3 bg-primary/5 rounded-lg">
                  <Clock size={18} className="text-primary mt-0.5" />
                  <div>
                    <strong>{t('rightToRestrict')}</strong>
                    <p className="text-sm text-fg/60">{t('rightToRestrictDesc')}</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3 flex items-center gap-2">
                <Clock size={20} /> 6. {t('dataRetention')}
              </h2>
              <p className="leading-relaxed">{t('retentionDesc')}</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">7. {t('dataSharing')}</h2>
              <p className="leading-relaxed">{t('dataSharingText')}</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">8. {t('dataSecurity')}</h2>
              <p className="leading-relaxed">{t('dataSecurityText')}</p>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li>{t('securityPoint1')}</li>
                <li>{t('securityPoint2')}</li>
                <li>{t('securityPoint3')}</li>
                <li>{t('securityPoint4')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3 flex items-center gap-2">
                <Globe size={20} /> 9. {t('internationalTransfers')}
              </h2>
              <p className="leading-relaxed">{t('internationalTransfersText')}</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3 flex items-center gap-2">
                <Mail size={20} /> 10. {t('contactDataOfficer')}
              </h2>
              <p className="leading-relaxed">{t('contactDesc')}</p>
              <div className="mt-3 p-4 bg-fg/5 rounded-lg">
                <p className="font-medium">COMBI.kz</p>
                <p>Email: <a href="mailto:khamit@combi.kz" className="text-primary hover:underline">khamit@combi.kz</a></p>
                <p>Телефон: +7 771 229 2371</p>
                <p className="text-sm text-fg/50 mt-2">{t('responseTime')}</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">11. {t('policyChanges')}</h2>
              <p className="leading-relaxed">{t('policyChangesText')}</p>
            </section>

            <div className="pt-4 mt-4 border-t border-fg/10 text-sm text-fg/50 text-center">
              <p> 2026 COMBI.kz. {t('allRightsReserved')}</p>
              <p className="mt-1">{t('complianceNote')}</p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;